import type { AdminRole, Attachment, Message, TodoPriority } from "../types";

export function formatDate(value?: string | null) {
  if (!value) return "无";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

export function toIso(value: string) {
  return new Date(value).toISOString();
}

export function toLocalInput(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export function localDateKey(value: Date | string) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function isToday(value?: string | null) {
  return Boolean(value && localDateKey(value) === localDateKey(new Date()));
}

export function todoPriorityLabel(priority: TodoPriority = "normal") {
  return { high: "高优先级", normal: "普通优先级", low: "低优先级" }[priority];
}

export function adminRoleRank(role: AdminRole) {
  return ["none", "support", "operator", "owner"].indexOf(role);
}

export function statusLabel(status: string) {
  const map: Record<string, string> = {
    unprocessed: "未处理",
    processed: "已处理",
    pending_confirmation: "待确认",
    archived: "已归档",
    ignored: "已忽略",
    converted_todo: "已转待办",
    converted_reminder: "已转提醒",
    converted_schedule: "已转日程",
    deleted: "已删除"
  };
  return map[status] || status;
}

export function readableError(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

export function formatBytes(value?: number | null) {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

export function messageDisplayText(message: Message, attachments: Attachment[] = []) {
  const displayName = message.display_name?.trim();
  if (displayName) return displayName;
  const content = message.content.trim();
  if (content) return content;

  const ids = message.attachment_ids || [];
  const firstAttachment = ids
    .map((id) => attachments.find((attachment) => attachment.id === id))
    .find((attachment): attachment is Attachment => Boolean(attachment));
  if (!firstAttachment) {
    return ids.length ? `${ids.length} 个文件` : "空白记录";
  }
  return ids.length === 1
    ? firstAttachment.filename
    : `${firstAttachment.filename} 等 ${ids.length} 个文件`;
}
